import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, LinkIcon, Loader2, UserPlus, Users } from 'lucide-react';
import { getInviter } from '../api/client';
import { RegistrationForm } from './RegistrationForm';

type LoaderState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; displayName: string; inviterId: string; successQrUrl?: string }
  | { status: 'limit_reached'; displayName: string; count: number; maxCount: number }
  | { status: 'invalid' };

export function RegistrationLoader() {
  const { inviterId } = useParams<{ inviterId: string }>();
  if (!inviterId) return <MissingInvite />;
  return <InviterResolver inviterId={inviterId} />;
}

function InviterResolver({ inviterId }: { inviterId: string }) {
  const [state, setState] = useState<LoaderState>({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      setState({ status: 'loading' });
      try {
        const result = await getInviter(inviterId);
        if (cancelled) return;
        if (result) {
          if (result.limitReached && result.maxCount !== undefined) {
            setState({
              status: 'limit_reached',
              displayName: result.displayName,
              count: result.count,
              maxCount: result.maxCount,
            });
          } else {
            setState({ status: 'ready', displayName: result.displayName, inviterId, successQrUrl: result.successQrUrl });
          }
        } else {
          setState({ status: 'invalid' });
        }
      } catch {
        setState({ status: 'invalid' });
      }
    }
    resolve();
    return () => { cancelled = true; };
  }, [inviterId]);

  switch (state.status) {
    case 'idle':
    case 'loading':
      return <LoadingState />;
    case 'ready':
      return <RegistrationForm inviterId={state.inviterId} displayName={state.displayName} successQrUrl={state.successQrUrl} />;
    case 'limit_reached':
      return (
        <LimitReached
          displayName={state.displayName}
          count={state.count}
          maxCount={state.maxCount}
        />
      );
    case 'invalid':
      return <InvalidLink />;
  }
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center">
        <Loader2 className="h-10 w-10 text-primary mx-auto mb-4 animate-spin" aria-hidden="true" />
        <p className="text-muted text-lg" role="status" aria-live="polite">正在加载报名页面…</p>
      </div>
    </div>
  );
}

export function MissingInvite() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-md bg-surface rounded-2xl shadow-xl p-10">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-warning-light">
          <UserPlus className="h-8 w-8 text-warning" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-3">需要邀请链接</h1>
        <p className="text-muted leading-relaxed text-base">
          您需要一个有效的邀请链接才能访问报名页面。请联系您的邀请人获取报名链接。
        </p>
      </div>
    </div>
  );
}

export function InvalidLink() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-md bg-surface rounded-2xl shadow-xl p-10">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-error-light">
          <AlertCircle className="h-8 w-8 text-error" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-3">邀请链接无效</h1>
        <p className="text-muted leading-relaxed text-base">
          此邀请链接无效或已过期。请检查链接地址，或联系您的邀请人获取新的链接。
        </p>
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted">
          <LinkIcon className="h-4 w-4" aria-hidden="true" />
          <span>无法验证您访问的链接</span>
        </div>
      </div>
    </div>
  );
}

function LimitReached({
  displayName,
  count,
  maxCount,
}: {
  displayName: string;
  count: number;
  maxCount: number;
}) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-md bg-surface rounded-2xl shadow-xl p-10">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-warning-light">
          <Users className="h-8 w-8 text-warning" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-3">报名名额已满</h1>
        <p className="text-muted leading-relaxed text-base">
          <span className="font-semibold text-foreground">{displayName}</span> 的邀请名额已达上限，此邀请链接暂时无法报名。
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-lg bg-background px-4 py-2 text-sm text-muted">
          已报名 <span className="font-semibold text-foreground">{count}</span> / {maxCount} 人
        </div>
        <p className="mt-6 text-sm text-muted">如有疑问，请联系您的邀请人。</p>
      </div>
    </div>
  );
}
